import React, { useState, useEffect } from 'react';
import {
  makeStyles,
  Container,
  Paper,
  Typography,
  Button,
  Stepper,
  Step,
  StepLabel,
  Grid,
  Card,
  CardContent,
  TextField,
  Checkbox,
  FormControlLabel,
  FormGroup,
  CircularProgress,
} from '@material-ui/core';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import SettingsIcon from '@material-ui/icons/Settings';
import WarningIcon from '@material-ui/icons/Warning';
import { transceive, autoConnect, connect, setAdminAuthenticated } from './actions';
import { byteToHexString } from './util';

// 辅助函数：字符串转十六进制
const stringToHex = (str) => {
  return byteToHexString(new TextEncoder().encode(str));
};

const useStyles = makeStyles((theme) => ({
  container: {
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
  },
  paper: {
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3),
  },
  title: {
    marginBottom: theme.spacing(3),
    color: theme.palette.primary.main,
    fontWeight: 'bold',
  },
  stepper: {
    backgroundColor: 'transparent',
    padding: theme.spacing(3, 0),
  },
  card: {
    marginBottom: theme.spacing(2),
  },
  buttonGroup: {
    display: 'flex',
    gap: theme.spacing(2),
    marginTop: theme.spacing(3),
  },
}));

export default function SetupWizard() {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const device = useSelector(state => state.device);
  const dispatch = useDispatch();

  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);

  // Step 1: Device detection
  const [deviceDetected, setDeviceDetected] = useState(false);

  // Step 2: PIN setup
  const [userPin, setUserPin] = useState('');
  const [confirmUserPin, setConfirmUserPin] = useState('');
  const [adminPin, setAdminPin] = useState('');
  const [confirmAdminPin, setConfirmAdminPin] = useState('');

  // Step 3: Feature selection
  const [features, setFeatures] = useState({
    fido2: true,
    piv: false,
    openpgp: false,
    oath: false,
  });

  // Step 4: Test results (动态根据选择的功能)
  // null: 未测试, true: 通过, false: 失败
  const [testResults, setTestResults] = useState({
    usb: null,
    fido2: null,
    piv: null,
    openpgp: null,
    oath: null,
  });

  const steps = [
    t('setupWizard.step1'),
    t('setupWizard.step2'),
    t('setupWizard.step3'),
    t('setupWizard.step4'),
    t('setupWizard.step5'),
  ];

  // 页面加载时尝试自动连接设备
  useEffect(() => {
    const tryAutoConnect = async () => {
      if (!device) {
        console.log('Attempting auto-connect to previously authorized device...');
        try {
          const connected = await dispatch(autoConnect());
          if (connected) {
            console.log('Auto-connect successful');
            enqueueSnackbar(t('setupWizard.deviceAutoConnected'), {
              variant: 'success',
            });
          } else {
            console.log('No previously authorized device found');
          }
        } catch (error) {
          console.error('Auto-connect failed:', error);
        }
      }
    };

    tryAutoConnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // 只在组件挂载时运行一次

  // 检测设备是否已初始化（是否使用默认PIN）
  useEffect(() => {
    const checkDeviceInitialized = async () => {
      if (!device) return;

      try {
        console.log('Checking if device is already initialized...');
        const defaultPin = '123456';
        const defaultPinHex = stringToHex(defaultPin);
        const lc = (defaultPinHex.length / 2).toString(16).padStart(2, '0');
        const verifyAPDU = `00200000${lc}${defaultPinHex}`;

        const response = await dispatch(transceive(verifyAPDU));
        const sw = response.slice(-4);

        if (sw === '9000') {
          // 默认PIN验证成功，说明设备未初始化
          console.log('Device is using default PIN - not initialized yet');
          enqueueSnackbar(t('setupWizard.defaultPinDetected'), {
            variant: 'info',
          });
        } else if (sw.startsWith('63C')) {
          // PIN错误，说明设备已经修改过PIN，已初始化
          console.log('Device has custom PIN - already initialized');
          enqueueSnackbar(t('setupWizard.alreadyInitialized'), {
            variant: 'warning',
            persist: true,
          });
        } else if (sw === '6983') {
          // PIN被锁定
          console.log('Device PIN is blocked');
          enqueueSnackbar(t('setupWizard.error.pinBlocked'), {
            variant: 'error',
            persist: true,
          });
        }
      } catch (error) {
        console.error('Failed to check device initialization status:', error);
      }
    };

    checkDeviceInitialized();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [device]); // 当设备连接时检测

  // 监听设备连接状态变化
  useEffect(() => {
    // 如果在配置过程中（step > 0）设备断开了，提示用户
    if (activeStep > 0 && !device) {
      enqueueSnackbar(t('setupWizard.error.deviceDisconnected'), {
        variant: 'error',
        persist: true,
      });
    }
  }, [device, activeStep, enqueueSnackbar, t]);

  const handleNext = async () => {
    if (activeStep === 0) {
      await checkDevice();
    } else if (activeStep === 1) {
      if (validatePins()) {
        await setupPins();
      }
    } else if (activeStep === 2) {
      await configureFeatures();
    } else if (activeStep === 3) {
      await runTests();
    } else {
      setActiveStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const checkDevice = async () => {
    setLoading(true);
    try {
      // If no device is connected, try to connect
      if (!device) {
        console.log('No device detected, prompting user to select device...');
        const connected = await dispatch(connect());

        if (!connected) {
          enqueueSnackbar(t('setupWizard.error.noDevice'), { variant: 'error' });
          return;
        }
      }

      // Simulate device check
      await new Promise(resolve => setTimeout(resolve, 1000));
      setDeviceDetected(true);
      setActiveStep(1);
      enqueueSnackbar(t('setupWizard.deviceDetected'), { variant: 'success' });
    } catch (error) {
      console.error('Device check error:', error);
      enqueueSnackbar(t('setupWizard.error.deviceCheck'), { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const validatePins = () => {
    if (userPin.length < 6 || userPin.length > 8) {
      enqueueSnackbar(t('setupWizard.error.invalidUserPin'), { variant: 'error' });
      return false;
    }
    if (userPin !== confirmUserPin) {
      enqueueSnackbar(t('setupWizard.error.userPinMismatch'), { variant: 'error' });
      return false;
    }
    if (adminPin.length !== 8) {
      enqueueSnackbar(t('setupWizard.error.invalidAdminPin'), { variant: 'error' });
      return false;
    }
    if (adminPin !== confirmAdminPin) {
      enqueueSnackbar(t('setupWizard.error.adminPinMismatch'), { variant: 'error' });
      return false;
    }
    if (userPin === adminPin) {
      enqueueSnackbar(t('setupWizard.error.samePins'), { variant: 'error' });
      return false;
    }
    return true;
  };

  const setupPins = async () => {
    setLoading(true);
    try {
      // 默认Admin PIN是 "123456"
      const defaultPin = '123456';

      // 1. 先用默认密码验证Admin applet
      console.log('Verifying with default PIN...');
      const defaultPinHex = stringToHex(defaultPin);
      const lc1 = (defaultPinHex.length / 2).toString(16).padStart(2, '0');
      const verifyAPDU = `00200000${lc1}${defaultPinHex}`;

      const verifyResponse = await dispatch(transceive(verifyAPDU));
      const verifySW = verifyResponse.slice(-4);

      if (verifySW !== '9000') {
        console.error('Default PIN verification failed, SW:', verifySW);
        if (verifySW.startsWith('63C')) {
          const retries = parseInt(verifySW.slice(-1), 16);
          enqueueSnackbar(t('setupWizard.error.wrongPin', { retries }), { variant: 'error' });
        } else if (verifySW === '6983') {
          enqueueSnackbar(t('setupWizard.error.pinBlocked'), { variant: 'error' });
        } else {
          enqueueSnackbar(t('setupWizard.error.defaultPinFailed'), { variant: 'error' });
        }
        return;
      }

      console.log('Default PIN verified successfully');

      // 2. 修改Admin PIN
      // 🔧 修复：Admin PIN修改命令只需要发送新PIN，不需要旧PIN
      // 因为已经在上一步通过VERIFY命令验证了旧PIN
      console.log('Changing Admin PIN...');
      const newAdminPinHex = stringToHex(adminPin);
      const lc2 = (newAdminPinHex.length / 2).toString(16).padStart(2, '0');
      const changeAdminPinAPDU = `00210000${lc2}${newAdminPinHex}`;

      const adminResponse = await dispatch(transceive(changeAdminPinAPDU));
      const adminSW = adminResponse.slice(-4);

      if (adminSW !== '9000') {
        console.error('Admin PIN change failed, SW:', adminSW);
        enqueueSnackbar(t('setupWizard.error.adminPinChangeFailed'), { variant: 'error' });
        return;
      }

      console.log('Admin PIN changed successfully');

      // 3. 修改OpenPGP的Admin PIN (PW3)
      // 先选择OpenPGP applet
      console.log('Selecting OpenPGP applet...');
      const selectOpenPGPAPDU = '00A4040006D27600012401';
      const selectResponse = await dispatch(transceive(selectOpenPGPAPDU));

      if (selectResponse.slice(-4) === '9000') {
        console.log('Changing OpenPGP Admin PIN (PW3)...');
        // OpenPGP默认Admin PIN是 "12345678"
        const defaultPW3 = '12345678';
        const oldPW3Hex = stringToHex(defaultPW3);
        const newPW3Hex = stringToHex(adminPin);
        const lc3 = ((oldPW3Hex.length + newPW3Hex.length) / 2).toString(16).padStart(2, '0');
        const changePW3APDU = `00D30083${lc3}${oldPW3Hex}${newPW3Hex}`;

        const pw3Response = await dispatch(transceive(changePW3APDU));
        if (pw3Response.slice(-4) === '9000') {
          console.log('OpenPGP Admin PIN changed successfully');
        } else {
          console.warn('OpenPGP Admin PIN change failed, SW:', pw3Response.slice(-4));
        }
      }

      // 4. 修改OpenPGP的User PIN (PW1)
      if (selectResponse.slice(-4) === '9000') {
        console.log('Changing OpenPGP User PIN (PW1)...');
        // OpenPGP默认User PIN是 "123456"
        const defaultPW1 = '123456';
        const oldPW1Hex = stringToHex(defaultPW1);
        const newPW1Hex = stringToHex(userPin);
        const lc4 = ((oldPW1Hex.length + newPW1Hex.length) / 2).toString(16).padStart(2, '0');
        const changePW1APDU = `00D30081${lc4}${oldPW1Hex}${newPW1Hex}`;

        const pw1Response = await dispatch(transceive(changePW1APDU));
        if (pw1Response.slice(-4) === '9000') {
          console.log('OpenPGP User PIN changed successfully');
        } else {
          console.warn('OpenPGP User PIN change failed, SW:', pw1Response.slice(-4));
        }
      }

      // 5. 修改PIV的PIN
      // 先选择PIV applet
      console.log('Selecting PIV applet...');
      const selectPIVAPDU = '00A4040009A00000030800001000';
      const selectPIVResponse = await dispatch(transceive(selectPIVAPDU));

      if (selectPIVResponse.slice(-4) === '9000') {
        console.log('Changing PIV PIN...');
        // PIV默认PIN是 "123456\xFF\xFF" (8字节)
        const defaultPIVPin = '123456\xFF\xFF';
        const oldPIVPinHex = stringToHex(defaultPIVPin);
        // PIV PIN必须是8字节，需要填充
        const newPIVPin = userPin.padEnd(8, '\xFF');
        const newPIVPinHex = stringToHex(newPIVPin);
        const changePIVPinAPDU = `0024008010${oldPIVPinHex}${newPIVPinHex}`;

        const pivResponse = await dispatch(transceive(changePIVPinAPDU));
        if (pivResponse.slice(-4) === '9000') {
          console.log('PIV PIN changed successfully');
        } else {
          console.warn('PIV PIN change failed, SW:', pivResponse.slice(-4));
        }
      }

      // 重新选择Admin applet以便后续操作
      await dispatch(transceive('00A4040005F000000000'));

      // 6. 用新的Admin PIN进行认证，自动登录
      console.log('Authenticating with new Admin PIN...');
      const adminPinHexForAuth = stringToHex(adminPin);
      const lc5 = (adminPinHexForAuth.length / 2).toString(16).padStart(2, '0');
      const authAPDU = `00200000${lc5}${adminPinHexForAuth}`;

      const authResponse = await dispatch(transceive(authAPDU));
      if (authResponse.slice(-4) === '9000') {
        console.log('Authentication successful - setting admin authenticated state');
        dispatch(setAdminAuthenticated(true));
        enqueueSnackbar(t('setupWizard.autoLoginSuccess'), { variant: 'success' });
      } else {
        console.warn('Authentication failed after PIN change, SW:', authResponse.slice(-4));
      }

      setActiveStep(2);
      enqueueSnackbar(t('setupWizard.pinsSet'), { variant: 'success' });
    } catch (error) {
      console.error('PIN setup error:', error);
      enqueueSnackbar(t('setupWizard.error.pinSetup'), { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const configureFeatures = async () => {
    setLoading(true);
    try {
      // TODO: Implement feature configuration
      await new Promise(resolve => setTimeout(resolve, 1000));
      setActiveStep(3);
      enqueueSnackbar(t('setupWizard.featuresConfigured'), { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(t('setupWizard.error.featureConfig'), { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const runTests = async () => {
    setLoading(true);
    try {
      // 1. 测试USB通信
      await new Promise(resolve => setTimeout(resolve, 300));
      const usbTestResult = await testUSBCommunication();
      setTestResults(prev => ({ ...prev, usb: usbTestResult }));

      if (!usbTestResult) {
        enqueueSnackbar(t('setupWizard.error.usbTestFailed'), { variant: 'error' });
        return;
      }

      // 2. 根据选择的功能进行测试
      if (features.fido2) {
        await new Promise(resolve => setTimeout(resolve, 300));
        const fido2TestResult = await testFIDO2Functionality();
        setTestResults(prev => ({ ...prev, fido2: fido2TestResult }));

        if (!fido2TestResult) {
          enqueueSnackbar(t('setupWizard.error.fido2TestFailed'), { variant: 'warning' });
        }
      }

      if (features.piv) {
        await new Promise(resolve => setTimeout(resolve, 300));
        const pivTestResult = await testPIVFunctionality();
        setTestResults(prev => ({ ...prev, piv: pivTestResult }));

        if (!pivTestResult) {
          enqueueSnackbar(t('setupWizard.error.pivTestFailed'), { variant: 'warning' });
        }
      }

      if (features.openpgp) {
        await new Promise(resolve => setTimeout(resolve, 300));
        const openpgpTestResult = await testOpenPGPFunctionality();
        setTestResults(prev => ({ ...prev, openpgp: openpgpTestResult }));

        if (!openpgpTestResult) {
          enqueueSnackbar(t('setupWizard.error.openpgpTestFailed'), { variant: 'warning' });
        }
      }

      if (features.oath) {
        await new Promise(resolve => setTimeout(resolve, 300));
        const oathTestResult = await testOATHFunctionality();
        setTestResults(prev => ({ ...prev, oath: oathTestResult }));

        if (!oathTestResult) {
          enqueueSnackbar(t('setupWizard.error.oathTestFailed'), { variant: 'warning' });
        }
      }

      setActiveStep(4);
      enqueueSnackbar(t('setupWizard.testsComplete'), { variant: 'success' });
    } catch (error) {
      console.error('Test error:', error);
      enqueueSnackbar(t('setupWizard.error.tests'), { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // 测试USB通信
  const testUSBCommunication = async () => {
    if (!device) {
      console.error('No device connected');
      return false;
    }

    try {
      // 尝试选择Admin applet来测试通信
      // Admin AID: F0 00 00 00 00
      const response = await dispatch(transceive('00A4040005F000000000'));

      // 只要有响应（不管成功还是失败），说明USB通信正常
      if (response && response.length >= 4) {
        const sw = response.slice(-4);
        console.log('USB communication test passed, SW:', sw);
        // 9000 = 成功
        return sw === '9000';
      }

      console.error('USB communication test failed: no valid response');
      return false;
    } catch (error) {
      console.error('USB communication test error:', error);
      return false;
    }
  };

  // 测试FIDO2功能
  const testFIDO2Functionality = async () => {
    if (!device) return false;

    try {
      // 尝试选择 FIDO2/U2F Applet
      // FIDO U2F AID: A0 00 00 06 47 2F 00 01
      const response = await dispatch(transceive('00A4040008A0000006472F0001'));

      // 检查是否成功选择（SW = 9000）
      if (response && response.length >= 4) {
        const sw = response.slice(-4);
        console.log('FIDO2 functionality test, SW:', sw);
        return sw === '9000';
      }

      console.error('FIDO2 test failed: unexpected response');
      return false;
    } catch (error) {
      console.error('FIDO2 functionality test failed:', error);
      return false;
    }
  };

  // 测试PIV功能
  const testPIVFunctionality = async () => {
    if (!device) return false;

    try {
      // 选择PIV Applet: A0 00 00 03 08 00 00 10 00
      const response = await dispatch(transceive('00A4040009A00000030800001000'));

      if (response && response.length >= 4) {
        const sw = response.slice(-4);
        console.log('PIV functionality test, SW:', sw);
        return sw === '9000';
      }

      console.error('PIV test failed: unexpected response');
      return false;
    } catch (error) {
      console.error('PIV functionality test failed:', error);
      return false;
    }
  };

  // 测试OpenPGP功能
  const testOpenPGPFunctionality = async () => {
    if (!device) return false;

    try {
      // 选择OpenPGP Applet: D2 76 00 01 24 01
      const response = await dispatch(transceive('00A4040006D27600012401'));

      if (response && response.length >= 4) {
        const sw = response.slice(-4);
        console.log('OpenPGP functionality test, SW:', sw);
        return sw === '9000';
      }

      console.error('OpenPGP test failed: unexpected response');
      return false;
    } catch (error) {
      console.error('OpenPGP functionality test failed:', error);
      return false;
    }
  };

  // 测试OATH功能
  const testOATHFunctionality = async () => {
    if (!device) return false;

    try {
      // 选择OATH Applet: A0 00 00 05 27 21 01 01
      const response = await dispatch(transceive('00A4040008A000000527210101'));

      if (response && response.length >= 4) {
        const sw = response.slice(-4);
        console.log('OATH functionality test, SW:', sw);
        return sw === '9000';
      }

      console.error('OATH test failed: unexpected response');
      return false;
    } catch (error) {
      console.error('OATH functionality test failed:', error);
      return false;
    }
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <div>
            <Typography variant="h6" gutterBottom>
              {t('setupWizard.welcome')}
            </Typography>
            <Typography variant="body1" paragraph>
              {t('setupWizard.welcomeDesc')}
            </Typography>
            <Card className={classes.card}>
              <CardContent>
                <Typography variant="body2">
                  📦 {t('setupWizard.whatYouNeed')}
                </Typography>
                <ul>
                  <li><Typography variant="body2">{t('setupWizard.need1')}</Typography></li>
                  <li><Typography variant="body2">{t('setupWizard.need2')}</Typography></li>
                  <li><Typography variant="body2">{t('setupWizard.need3')}</Typography></li>
                </ul>
              </CardContent>
            </Card>

            {/* 设备连接状态提示 */}
            {device ? (
              <Card style={{ marginTop: 16, backgroundColor: '#e8f5e9', borderLeft: '4px solid #4caf50' }}>
                <CardContent>
                  <Typography variant="body1" style={{ display: 'flex', alignItems: 'center', color: '#2e7d32' }}>
                    <CheckCircleIcon style={{ marginRight: 8, color: '#4caf50' }} />
                    {t('setupWizard.deviceConnected')}
                  </Typography>
                </CardContent>
              </Card>
            ) : (
              <Card style={{ marginTop: 16, backgroundColor: '#fff3e0', borderLeft: '4px solid #ff9800' }}>
                <CardContent>
                  <Typography variant="body2" style={{ color: '#e65100' }}>
                    ⚠️ {t('setupWizard.noDeviceHint')}
                  </Typography>
                </CardContent>
              </Card>
            )}
          </div>
        );

      case 1:
        return (
          <div>
            <Typography variant="h6" gutterBottom>
              {t('setupWizard.setupPins')}
            </Typography>
            <Typography variant="body2" color="textSecondary" paragraph>
              {t('setupWizard.pinsDesc')}
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom>
                  {t('setupWizard.userPinSection')}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  required
                  type="password"
                  label={t('setupWizard.userPin')}
                  value={userPin}
                  onChange={(e) => setUserPin(e.target.value)}
                  inputProps={{ maxLength: 8 }}
                  helperText={t('setupWizard.userPinHelper')}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  required
                  type="password"
                  label={t('setupWizard.confirmUserPin')}
                  value={confirmUserPin}
                  onChange={(e) => setConfirmUserPin(e.target.value)}
                  inputProps={{ maxLength: 8 }}
                  error={!!(confirmUserPin && userPin !== confirmUserPin)}
                  helperText={
                    confirmUserPin && userPin !== confirmUserPin
                      ? t('setupWizard.error.userPinMismatch')
                      : ''
                  }
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom style={{ marginTop: 16 }}>
                  {t('setupWizard.adminPinSection')}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  required
                  type="password"
                  label={t('setupWizard.adminPin')}
                  value={adminPin}
                  onChange={(e) => setAdminPin(e.target.value)}
                  inputProps={{ maxLength: 8 }}
                  helperText={t('setupWizard.adminPinHelper')}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  required
                  type="password"
                  label={t('setupWizard.confirmAdminPin')}
                  value={confirmAdminPin}
                  onChange={(e) => setConfirmAdminPin(e.target.value)}
                  inputProps={{ maxLength: 8 }}
                  error={!!(confirmAdminPin && adminPin !== confirmAdminPin)}
                  helperText={
                    confirmAdminPin && adminPin !== confirmAdminPin
                      ? t('setupWizard.error.adminPinMismatch')
                      : ''
                  }
                />
              </Grid>
            </Grid>
          </div>
        );

      case 2:
        return (
          <div>
            <Typography variant="h6" gutterBottom>
              {t('setupWizard.selectFeatures')}
            </Typography>
            <Typography variant="body2" color="textSecondary" paragraph>
              {t('setupWizard.featuresDesc')}
            </Typography>

            <FormGroup>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={features.fido2}
                    onChange={(e) => setFeatures({ ...features, fido2: e.target.checked })}
                    color="primary"
                  />
                }
                label={
                  <div>
                    <Typography variant="body1">
                      <strong>FIDO2/U2F</strong> ({t('setupWizard.recommended')})
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {t('setupWizard.fido2Desc')}
                    </Typography>
                  </div>
                }
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={features.piv}
                    onChange={(e) => setFeatures({ ...features, piv: e.target.checked })}
                    color="primary"
                  />
                }
                label={
                  <div>
                    <Typography variant="body1"><strong>PIV Smart Card</strong></Typography>
                    <Typography variant="body2" color="textSecondary">
                      {t('setupWizard.pivDesc')}
                    </Typography>
                  </div>
                }
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={features.openpgp}
                    onChange={(e) => setFeatures({ ...features, openpgp: e.target.checked })}
                    color="primary"
                  />
                }
                label={
                  <div>
                    <Typography variant="body1"><strong>OpenPGP</strong></Typography>
                    <Typography variant="body2" color="textSecondary">
                      {t('setupWizard.openpgpDesc')}
                    </Typography>
                  </div>
                }
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={features.oath}
                    onChange={(e) => setFeatures({ ...features, oath: e.target.checked })}
                    color="primary"
                  />
                }
                label={
                  <div>
                    <Typography variant="body1"><strong>OATH (TOTP/HOTP)</strong></Typography>
                    <Typography variant="body2" color="textSecondary">
                      {t('setupWizard.oathDesc')}
                    </Typography>
                  </div>
                }
              />
            </FormGroup>
          </div>
        );

      case 3:
        return (
          <div>
            <Typography variant="h6" gutterBottom>
              {t('setupWizard.testing')}
            </Typography>
            <Typography variant="body2" color="textSecondary" paragraph>
              {t('setupWizard.testingDesc')}
            </Typography>

            {/* USB通信测试 - 总是显示 */}
            <Card className={classes.card}>
              <CardContent>
                <Typography variant="body1">
                  {testResults.usb === true ? '✅' : testResults.usb === false ? '❌' : '⏳'} {t('setupWizard.testUsb')}
                </Typography>
              </CardContent>
            </Card>

            {/* 根据选择的功能显示测试项 */}
            {features.fido2 && (
              <Card className={classes.card}>
                <CardContent>
                  <Typography variant="body1">
                    {testResults.fido2 === true ? '✅' : testResults.fido2 === false ? '❌' : '⏳'} {t('setupWizard.testFido2')}
                  </Typography>
                </CardContent>
              </Card>
            )}

            {features.piv && (
              <Card className={classes.card}>
                <CardContent>
                  <Typography variant="body1">
                    {testResults.piv === true ? '✅' : testResults.piv === false ? '❌' : '⏳'} {t('setupWizard.testPiv')}
                  </Typography>
                </CardContent>
              </Card>
            )}

            {features.openpgp && (
              <Card className={classes.card}>
                <CardContent>
                  <Typography variant="body1">
                    {testResults.openpgp === true ? '✅' : testResults.openpgp === false ? '❌' : '⏳'} {t('setupWizard.testOpenPGP')}
                  </Typography>
                </CardContent>
              </Card>
            )}

            {features.oath && (
              <Card className={classes.card}>
                <CardContent>
                  <Typography variant="body1">
                    {testResults.oath === true ? '✅' : testResults.oath === false ? '❌' : '⏳'} {t('setupWizard.testOath')}
                  </Typography>
                </CardContent>
              </Card>
            )}
          </div>
        );

      case 4:
        return (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <CheckCircleIcon style={{ fontSize: 80, color: '#4caf50' }} />
            <Typography variant="h5" style={{ marginTop: 20, marginBottom: 10 }}>
              {t('setupWizard.complete')}
            </Typography>
            <Typography variant="body1" color="textSecondary" paragraph>
              {t('setupWizard.completeDesc')}
            </Typography>

            <Card className={classes.card} style={{ marginTop: 24, textAlign: 'left' }}>
              <CardContent>
                <Typography variant="body2">
                  <strong>{t('setupWizard.nextSteps')}:</strong>
                </Typography>
                <ul>
                  <li><Typography variant="body2">{t('setupWizard.next1')}</Typography></li>
                  <li><Typography variant="body2">{t('setupWizard.next2')}</Typography></li>
                  <li><Typography variant="body2">{t('setupWizard.next3')}</Typography></li>
                </ul>
              </CardContent>
            </Card>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <Container className={classes.container} maxWidth="md">
      <Paper className={classes.paper}>
        <Typography variant="h4" className={classes.title}>
          <SettingsIcon style={{ verticalAlign: 'middle', marginRight: 8 }} />
          {t('setupWizard.title')}
        </Typography>

        {/* 设备断开警告 */}
        {activeStep > 0 && !device && (
          <Card style={{ marginBottom: 16, backgroundColor: '#fff3cd', borderLeft: '4px solid #ff9800' }}>
            <CardContent>
              <Typography variant="body1" style={{ display: 'flex', alignItems: 'center', color: '#856404' }}>
                <WarningIcon style={{ marginRight: 8, color: '#ff9800' }} />
                {t('setupWizard.error.deviceDisconnected')}
              </Typography>
            </CardContent>
          </Card>
        )}

        <Stepper activeStep={activeStep} className={classes.stepper}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        <div style={{ marginTop: 24 }}>
          {renderStepContent(activeStep)}
        </div>

        <div className={classes.buttonGroup}>
          {activeStep > 0 && activeStep < 4 && (
            <Button onClick={handleBack} disabled={loading}>
              {t('common.back')}
            </Button>
          )}
          <div style={{ flex: 1 }} />
          {activeStep < 4 && (
            <Button
              variant="contained"
              color="primary"
              onClick={handleNext}
              disabled={loading}
              startIcon={loading && <CircularProgress size={20} />}
            >
              {activeStep === 3 ? t('setupWizard.runTests') : t('common.next')}
            </Button>
          )}
        </div>
      </Paper>
    </Container>
  );
}
